import type {
  FC,
  StyleHTMLAttributes,
} from 'react';
import { Box } from '@metropolis-io/components';
import type { BoxProps } from '@metropolis-io/components';
import { TabLabel } from './TabLabel';
import { useTabContext } from './useTabContext';
import {
  defaultLabelRender,
  defaultTabBarRender,
  defaultTabContentRender,
} from './defaultTabRenderers';
import type {
  StyledTabLabelProps,
  TabLabelProps,
  RenderTabLabelFn,
  RenderTabBarFn,
  RenderTabContentFn,
} from './Tabs.types';

export interface TabControllerProps extends BoxProps {
  tabLabelComponent?: FC<TabLabelProps>;
  renderTabLabel?: RenderTabLabelFn;
  renderTabBar?: RenderTabBarFn;
  renderTabContent?: RenderTabContentFn;
  tabBarProps?: BoxProps;
  tabContainerProps?: BoxProps;
  tabLabelProps?: StyledTabLabelProps;
  tabLabelActiveStyle?: StyleHTMLAttributes<HTMLButtonElement>;
}

export function TabRenderer({
  tabLabelComponent: TabLabelComponent = TabLabel,
  renderTabLabel,
  renderTabBar,
  renderTabContent,
  tabBarProps = {},
  tabContainerProps = {},
  tabLabelProps = {},
  tabLabelActiveStyle,
  ...wrapperProps
}: TabControllerProps) {
  const tabCtx = useTabContext();

  const labelRenderer = renderTabLabel ?? defaultLabelRender;
  const tabNodes = tabCtx.tabs.map((tabData) => labelRenderer({
    tabData,
    Component: TabLabelComponent,
    props: tabLabelProps,
    context: tabCtx,
  }));

  const tabBarRenderer = renderTabBar ?? defaultTabBarRender;
  const tabContentRender = renderTabContent ?? defaultTabContentRender;

  return (
    <Box
      flexDirection="column"
      width="100%"
      {...wrapperProps}
    >
      {tabBarRenderer({
        tabNodes,
        props: tabBarProps,
        context: tabCtx,
      })}
      {tabContentRender({
        props: tabContainerProps,
        context: tabCtx,
      })}
    </Box>
  );
}
